import {
  groupBy,
  sortBy,
  reduce,
  get,
  isArray,
  last,
  concat,
  pickBy,
  keyBy,
  find,
  remove,
  isNil,
  isEmpty,
  filter,
  defaultTo,
  compact,
} from 'lodash';
import { rawTimeZones } from '@vvo/tzdb';
import route from '../constants/routes';
import {
  TP,
  dateFormats,
  PERFORMANCE_TYPES_ID,
  contactsIdentifiers,
  MEDIA_TAGS_TYPES_IDS,
  TAGS_AVAILABILITY_TYPES,
  VIDEO_ATTRIBUTE_TYPES_IDS,
  LOGO_TYPE,
  PRODUCTION_ADDED_IDENTIFIERS_IDS,
} from '../constants/consts';
import { PING_URL } from '../constants/endpointConstants';
import { Router } from '../src/i18n';
// eslint-disable-next-line import/no-cycle
import { getOptions } from '../containers/InteractiveFilterPanel/aggregationUtil';
import { createDate } from './date';
import { getAxiosInstance } from './httpInstance';
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';

require('dotenv').config();

let pingInterval;
const TIME_INTERVAL = 60000;

export const createSelectFieldOptions = (
  arrayOfObjects,
  valueName,
  labelName = null,
  customProps = {},
  additionalProp,
) => {
  const label = labelName || valueName;
  let options = [];
  if (isArray(arrayOfObjects)) {
    options = arrayOfObjects.map(item => ({
      value: get(item, valueName, null),
      label: (get(item, label, '') || '').toString(),
      ...customProps,
      ...(additionalProp && item),
    }));
  }
  return options;
};

const emptyString = '';
const getNotNullFieldValue = (v, defaultValue) => (v === null ? defaultValue : v);

export const getOption = (object, valueName = 'id', labelName = 'name', props = {}, allProps) => {
  let option;
  if (object) {
    option = {
      ...props,
      ...(allProps && object),
      // lodash 'get' returns default value only if the value is resolved as undefined
      value: getNotNullFieldValue(get(object, valueName, emptyString), emptyString),
      label: getNotNullFieldValue(get(object, labelName, emptyString), emptyString),
    };
  }
  return option;
};

export const dateSendFormat = date => {
  const momentData = createDate(date);
  return momentData.isValid() ? momentData.format(dateFormats.basic) : null;
};

export const dateShowFormat = date => createDate(date).format('MMM D, YYYY');

export const formatDateFromString = (date, format) => (date ? createDate(date).format(format) : null);

export const getRoutePartWithSlug = infoObject => `${get(infoObject, 'slug', '')}-${get(infoObject, 'id')}`;

export const getRouteWithSlug = (mainRoute, infoObject) => `${mainRoute}/${getRoutePartWithSlug(infoObject)}`;

export const getRouteWithCountryAndSlug = (mainRoute, infoObject) =>
  `${mainRoute}/${get(infoObject, 'country.slug')}/${getRoutePartWithSlug(infoObject)}`;

export const getRouteWithYearCountryAndSlug = (mainRoute, infoObject) =>
  `${mainRoute}/${get(infoObject, 'year')}/${get(infoObject, 'country.slug')}/${getRoutePartWithSlug(infoObject)}`;

export const getDynamicRouteProps = (rootRoute, object) => ({
  href: route.DYNAMIC_ID_ROUTE(rootRoute),
  as: getRouteWithSlug(rootRoute, object),
});

export const getDynamicRoutePropsForNewTab = (rootRoute, object, target) => ({
  ...getDynamicRouteProps(rootRoute, object),
  target: target || '_blank',
});

export const removeWithTranslationProps = props => {
  const strippedProps = { ...props };
  delete strippedProps.defaultNS;
  delete strippedProps.i18n;
  delete strippedProps.i18nOptions;
  delete strippedProps.lng;
  delete strippedProps.reportNS;
  delete strippedProps.t;
  delete strippedProps.tReady;
  delete strippedProps.forwardedRef;
  delete strippedProps.subpath;
  delete strippedProps.signed_login_token;
  return strippedProps;
};

export const getPageParamFromQuery = (path, query) => {
  const cleanQuery = removeWithTranslationProps(query);
  const pageParams = Object.entries(cleanQuery);
  const stringifiedPageParams = `?${pageParams.map(([key, val]) => `${key}=${val}`).join('&')}`;
  return `${path}${pageParams.length ? stringifiedPageParams : ''}`;
};

export const getIdFromSlugRoute = idWithSlug => idWithSlug && last(idWithSlug.split('-'));

export const getPeriodFromDates = (dateFrom, dateTo) => {
  if (dateFrom && dateTo) {
    const yearFrom = createDate(dateFrom).year();
    const yearTo = createDate(dateTo).year();
    return yearFrom === yearTo ? yearFrom : `${yearFrom} - ${yearTo}`;
  }

  if (dateFrom) {
    return createDate(dateFrom).year();
  }

  return undefined;
};

export const separateExcludedIncludedCountries = countries => {
  const included = [];
  const excluded = [];
  countries.forEach(country => {
    if (get(country, 'isExcluded')) {
      excluded.push(country);
    } else {
      included.push(country);
    }
  });
  return { included, excluded };
};

const pattern = /^(https?:\/\/)/;

export const addProtocolIfNeed = href => (pattern.test(href) ? href : `http://${href}`);

export const reduceCollectionToTypes = (collection, types, reducer) => types.map(item => reducer(collection, item));

export const getDateRangeFormat = (minDate, maxDate) =>
  `${createDate(minDate).format('DD MMM YY')} - ${createDate(maxDate).format('DD MMM YY')}`;

export const getShortMonthYearFormat = date => createDate(date).format(dateFormats.shortTextMonthAndYear);

export const getShortDateRangeFormat = (minDate, maxDate) => {
  const minDateMoment = createDate(minDate);
  const maxDateMoment = createDate(maxDate);

  if (!minDate && maxDate) {
    return getShortMonthYearFormat(maxDate);
  }

  if (!maxDate) {
    return getShortMonthYearFormat(maxDate);
  }

  if (
    minDateMoment.format(dateFormats.shortTextMonthAndYear) === maxDateMoment.format(dateFormats.shortTextMonthAndYear)
  ) {
    return getShortMonthYearFormat(maxDate);
  }

  if (minDateMoment.format(dateFormats.year) === maxDateMoment.format(dateFormats.year)) {
    return `${createDate(minDate).format('MMM')} - ${getShortMonthYearFormat(maxDate)}`;
  }

  if (minDateMoment.format(dateFormats.year) !== maxDateMoment.format(dateFormats.year)) {
    return `${getShortMonthYearFormat(minDate)} - ${getShortMonthYearFormat(maxDate)}`;
  }

  return '';
};

export const sortOptionToLabel = (option, t) => {
  const direction = option.startsWith('-') ? `↑` : `↓`;
  const optionName = `${TP}.SORT_${option
    .replace(/^-/, '')
    .split('.')
    .join('_')
    .toUpperCase()}`;
  return `${t(optionName)} ${t(direction)}`;
};

export const createSortingOptions = (options, t, reverse = true) => {
  const sortingOptions = [];
  options.forEach(sortOption => {
    sortingOptions.push({
      value: sortOption,
      label: sortOptionToLabel(sortOption, t),
    });
    if (reverse)
      sortingOptions.push({
        value: `-${sortOption}`,
        label: sortOptionToLabel(`-${sortOption}`, t),
      });
  });
  return sortingOptions;
};

export const setIntervalForPing = () => {
  clearIntervalForPing();
  pingInterval = setInterval(() => {
    ping();
  }, TIME_INTERVAL);
};

const ping = async () => {
  const headers = {
    'Content-Type': 'application/json',
  };
  const axiosInstance = getAxiosInstance();

  const url = `${process.env.FRONTBASE_URL}/api/${PING_URL}`;
  let payload;
  try {
    payload = await axiosInstance(url, {
      method: 'PUT',
      headers,
      data: '',
    });
  } catch (err) {
    payload = err.response;
    if (err.response.status === 401) {
      Router.push(`${route.LOGOUT}?fromError=true`);
    }
  }
  return payload?.data;
};

export const clearIntervalForPing = () => {
  if (pingInterval) {
    clearInterval(pingInterval);
  }
};

export const getCastGroupByRole = castGroupByRole => {
  let subObject;
  if (castGroupByRole.undefined) {
    subObject = groupBy(castGroupByRole.undefined, 'workRoleReference');
  }
  const newCastGroupByRole = { ...castGroupByRole };
  delete newCastGroupByRole.undefined;
  return { ...newCastGroupByRole, ...subObject };
};

export const getOtherColumnData = crewGroupByRole => {
  let subObject;
  if (crewGroupByRole.undefined) {
    subObject = groupBy(crewGroupByRole.undefined, 'workRoleReference');
  }
  const newOtherGroupByRole = { ...crewGroupByRole };
  return { ...newOtherGroupByRole, ...subObject };
};

export const getCrewGroupByRole = crewGroupByRole => {
  const newCrewGroupByRole = { ...crewGroupByRole };
  delete newCrewGroupByRole.undefined;
  return newCrewGroupByRole;
};

export const getFilteredTourDates = dates =>
  dates.filter(date => {
    const filteredPerformanceTypes = date.performanceTypes.filter(item => item.slug === 'tour');
    return filteredPerformanceTypes?.length > 0;
  });

export const yearsToShowCountOptions = (fromYear = 1996) => {
  const years = [];
  // eslint-disable-next-line no-plusplus
  for (let i = new Date().getFullYear() + 5; i >= fromYear; i--) {
    years.push({ value: i, label: `${i}` });
  }
  return years;
};

export const getProductionTypeWithDates = production => {
  const dates = get(production, 'dates');
  let active = false;
  if (dates !== null) {
    const sortedDates = sortBy(
      dates?.filter(date => typeof get(date, 'date') === 'string'),
      [date => date.date],
    );

    sortedDates.forEach(date => {
      const dateValue = get(date, 'date');
      const momentDate = createDate(dateValue, dateFormats.basic);
      if (momentDate && momentDate.isValid()) {
        active = momentDate.format(dateFormats.basic) >= createDate().format(dateFormats.basic);
      }
    });
  }
  return active ? 'live' : 'past';
};

const transformDate = (date, momentDate, currentDate, isPerformed) => {
  const active = momentDate.format(dateFormats.basic) === createDate().format(dateFormats.basic);
  const past = momentDate.isBefore(currentDate);
  const cancelled = get(date, 'performanceTypes', []).reduce(
    (item, performanceType) => item || get(performanceType, 'id') === PERFORMANCE_TYPES_ID.CANCELLED,
    false,
  );
  const matinee = get(date, 'performanceTypes', []).reduce(
    (item, performanceType) => item || get(performanceType, 'id') === PERFORMANCE_TYPES_ID.MATINEE,
    false,
  );
  const tour = get(date, 'performanceTypes', []).reduce(
    (item, performanceType) => item || get(performanceType, 'id') === PERFORMANCE_TYPES_ID.TOUR,
    false,
  );
  const performed = date?.performed || isPerformed(date);
  return {
    active,
    past,
    cancelled,
    matinee,
    tour,
    date: momentDate,
    month: momentDate.format(dateFormats.productionMonth),
    performed,
  };
};

export const getGroupedProductionsDatesWithinRange = (production, range, isPerformed = () => false) => {
  const { date_from, date_to } = range;
  const dates = get(production, 'dates');
  const currentDate = createDate().format(dateFormats.basic);
  let groupedDates = [];
  if (dates !== null) {
    const sortedDates = sortBy(
      dates.filter(date => typeof get(date, 'date') === 'string'),
      [date => date.date],
    );

    groupedDates = groupBy(
      sortedDates
        .map(date => {
          const dateValue = get(date, 'date');
          const momentDate = createDate(dateValue, dateFormats.basic);
          const dateFrom = createDate(date_from, dateFormats.basic);
          const dateTo = createDate(date_to, dateFormats.basic);
          if (
            momentDate.format(dateFormats.basic) >= dateFrom.format(dateFormats.basic) &&
            momentDate.format(dateFormats.basic) <= dateTo.format(dateFormats.basic)
          ) {
            return transformDate(date, momentDate, currentDate, isPerformed);
          }
          return null;
        })
        .filter(Boolean),
      'month',
    );
  }
  return groupedDates;
};

export const getGroupedProductionsDateForTheCard = (production, isFromToday, isPerformed = () => false) => {
  const dates = get(production, 'dates');
  const currentDate = createDate().format(dateFormats.basic);
  let groupedDates = [];
  if (dates !== null) {
    const sortedDates = sortBy(
      dates?.filter(date => typeof get(date, 'date') === 'string'),
      [date => date.date],
    );
    const datesTransformedValues = [];
    sortedDates.forEach(date => {
      const dateValue = get(date, 'date');
      const momentDate = createDate(dateValue, dateFormats.basic);
      if (isFromToday && momentDate.format(dateFormats.basic) >= createDate().format(dateFormats.basic)) {
        datesTransformedValues.push(transformDate(date, momentDate, currentDate, isPerformed));
      }
      if (!isFromToday) {
        datesTransformedValues.push(transformDate(date, momentDate, currentDate, isPerformed));
      }
      return null;
    });

    groupedDates = groupBy(datesTransformedValues, 'month');
  }
  return groupedDates;
};

export const getChipsForProductionCard = types => {
  const chipLabels = [
    PERFORMANCE_TYPES_ID.WORLD_PREMIERE,
    PERFORMANCE_TYPES_ID.NATIONAL_PREMIERE,
    PERFORMANCE_TYPES_ID.CONCERT,
    PERFORMANCE_TYPES_ID.NEW_PRODUCTION,
    PERFORMANCE_TYPES_ID.CANCELLED,
    PERFORMANCE_TYPES_ID.FESTIVAL,
    PERFORMANCE_TYPES_ID.DOUBLE_BILL,
    PERFORMANCE_TYPES_ID.RECITAL,
  ];
  const reduceDateType = (performanceTypes, type) =>
    reduce(performanceTypes, (result, performanceType) => result || get(performanceType, 'id') === type, false);
  return reduceCollectionToTypes(types, chipLabels, reduceDateType);
};

export const fullDateConversion = (date, lang = 'en', withDay = false) => {
  const options = withDay
    ? { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
    : { year: 'numeric', month: 'long', day: 'numeric' };
  return date && new Date(date).toLocaleDateString(lang, options);
};

export const profileDateConversion = (personal, lang, keys = []) => {
  if (keys.length > 0) {
    let dob = get(personal, keys[0]);
    const cityB = get(personal, keys[1]);
    const countryB = get(personal, keys[2]);
    let dod = get(personal, keys[3]);
    const cityD = get(personal, keys[4]);
    const countryD = get(personal, keys[5]);
    dob = fullDateConversion(dob, lang);
    dod = fullDateConversion(dod, lang);
    let locationB = cityB ? cityB?.name : '';
    locationB += countryB ? (cityB ? `, ${countryB?.name}` : `${countryB?.name}`) : '';
    let locationD = cityD ? cityD?.name : '';
    locationD += countryD ? (cityD ? `, ${countryD?.name}` : `${countryD?.name}`) : '';
    let profileDate = '';
    if (dob) {
      profileDate += dob;
      profileDate = locationB && `${profileDate} (${locationB}) `;
    } else if (dod) {
      profileDate += '? -';
      profileDate += dod;
      profileDate = locationD && `${profileDate} in ${locationD}`;
      return profileDate;
    } else {
      return null;
    }
    if (dod) {
      profileDate += ' - ';
      profileDate += dod;
      profileDate = locationD && `${profileDate} (${locationD}) `;
    }
    return profileDate;
  }
  return 'Invalid keys';
};

export const buildSocialLinks = contacts =>
  contacts &&
  contacts
    .filter(c => [contactsIdentifiers.sns, contactsIdentifiers.other].includes(get(c, 'contactType.id')))
    .map(item => ({
      id: get(item, 'id'),
      link: get(item, 'value'),
      type: get(item, 'sns') || 'other',
    }));

export const swap = (arr, from, to) => arr.splice(from, 1, arr.splice(to, 1, arr[from])[0]);

/**
 * Get artist profile title
 *
 * @param profile
 * @returns {string}
 */
export const getFullProfileTitle = profile => {
  const name = get(profile, 'name');
  const profession =
    get(profile, 'profession.qualification.name') || get(profile, 'profession.name') || get(profile, 'summary');
  return [name, profession].filter(item => item).join(', ');
};

export const getEditTitle = (t, title) => {
  const result = [];
  if (title && typeof title === 'string') {
    result.push(title);
  }
  if (t && typeof t === 'function') {
    result.push(t(`${TP}.m_EDIT`));
  }
  return result.join(', ');
};

export const changeLanguageForUrl = (languageCode, url) => {
  try {
    const newUrl = new URL(url);
    if (!newUrl.pathname) {
      return false;
    }
    // get the pathnames
    const paths = newUrl.pathname.split('/');
    // check if language code is matching
    if (paths[paths.length - 1] === languageCode) {
      return false;
    }
    // replace the language code
    paths.splice(paths.length - 1, 1, languageCode);
    newUrl.pathname = paths.join('/');
    return newUrl.toString();
  } catch (err) {
    return false;
  }
};

export const getQueryParamFromPath = (path, paramName) => {
  const regex = new RegExp(`[?&]${paramName}(=([^&#]*)|&|#|$)`);
  const results = regex.exec(path);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
};

export const scrollToInvalidField = () => {
  setTimeout(() => {
    const invalidElements = document.getElementsByClassName('Mui-error');
    if (invalidElements.length) {
      invalidElements[0].scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  }, 0);
};

export const buildLocationDetailsProduction = production => {
  const venues = get(production, 'venues', []);
  const festivals = get(production, 'festivals', null);
  const company = production?.companies.find(c => c.id === production?.company?.id);
  const locationDetails = [];
  const coProducedCompanies = production?.companies?.filter(c => c?.is_partner && c?.id !== company.id);

  if (!isEmpty(company)) {
    locationDetails.push({
      label: 'company',
      value: company.name,
      href: `${route.COMPANIES}/[country]/[id]/[[...action]]`,
      as: `${route.COMPANIES}/${get(company, 'country.slug', '')}/${getRoutePartWithSlug(company)}`,
    });
  }

  if (!isEmpty(coProducedCompanies)) {
    coProducedCompanies.map(coCompany => {
      locationDetails.push({
        label: 'co-produced',
        type: `${TP}.FN_CO_PRODUCED`,
        value: coCompany?.name,
        href: `${route.COMPANIES}/[country]/[id]/[[...action]]`,
        as: `${route.COMPANIES}/${get(coCompany, 'country.slug', '')}/${getRoutePartWithSlug(coCompany)}`,
      });
    });
  }

  if (festivals && festivals.length > 0) {
    festivals.map(festival => {
      locationDetails.push({
        label: 'festival',
        type: `${TP}.FN_PERFORMACE_TYPE_PART_OF_FESTIVAL`,
        value: festival?.name,
        href: `${route.FESTIVALS}/[year]/[country]/[id]/[[...action]]`,
        as: `${route.FESTIVALS}/${get(festival, 'year')}/${get(festival, 'country.slug')}/${getRoutePartWithSlug(
          festival,
        )}`,
      });
    });
  }

  if (venues?.length > 0) {
    const locationAddress = `${venues[0]?.name}, ${getCountryCity(venues[0])}`;
    locationDetails.push({
      label: 'location',
      value: venues?.length > 1 ? `${locationAddress} (+${venues?.length - 1} more)` : locationAddress,
      href: `${route.VENUES}/[country]/[id]/[[...action]]`,
      as: getRouteWithSlug(`${route.VENUES}/${get(venues[0], 'country.slug')}`, venues[0]),
    });
  }
  return locationDetails;
};

export const getCastAndCrew = production => concat(get(production, 'crew', []), get(production, 'casts', []));

export const getImageUrl = image => image.medium || image.large || image.small || image.original;

export const buildContacts = contacts => {
  const contactData = contacts.map(item => {
    const updatedItem = item ? { ...item } : {};
    if (item?.contactType?.id === 1) {
      updatedItem.href = `mailto:${item.value}`;
    } else if (item?.contactType?.id === 3) {
      updatedItem.href = item.value;
      updatedItem.target = '_blank';
    }
    updatedItem.contactTypeId = item?.contactType?.id;
    return updatedItem;
  });

  const filteredData = contactData.filter(item => item?.contactType?.id !== 2);

  const sortContacts = sortBy(filteredData, 'contactType.id');
  const data = groupBy(sortContacts, 'contactType.id');
  return data;
};

export const getContactsStateForUpdate = (formValue, contacts) => {
  const staticFieldsObj = pickBy(formValue, (_, key) => !key.includes('other'));
  const otherFieldsArr = pickBy(formValue, (_, key) => key.includes('other')).other;
  const valuesObj = otherFieldsArr.length
    ? { ...staticFieldsObj, ...keyBy(otherFieldsArr, el => el.name) }
    : staticFieldsObj;
  const newContacts = {};
  const updatedContacts = {};
  const deletedContactsIds = [];

  // if field array item was removed
  contacts.forEach(contact => {
    if (contact?.contactType?.id === contactsIdentifiers.other && !valuesObj[contact?.name]) {
      deletedContactsIds.push(contact?.id);
    }
  });

  Object.keys(valuesObj).forEach(key => {
    const field = get(valuesObj, key);
    if (field?.value) {
      const initField = contacts.find(el => el.name === key);
      if (initField && initField.value !== field?.value) {
        updatedContacts[key] = field;
      } else if (!initField) {
        newContacts[key] = field;
      }
    } else if (field?.id) {
      deletedContactsIds.push(field?.id);
    }
  });
  return { newContacts, updatedContacts, deletedContactsIds };
};

export const checkIfValueExist = (list, value) => find(list, { slug: value });

export const getArtisLabelNameWithRole = profile => {
  const profession = profile?.profession?.qualification?.name || profile?.profession?.name;
  return profession ? `${profile?.name} (${profession})` : profile?.name;
};

export const getProductionLabelKeys = tagId => {
  switch (tagId) {
    case PERFORMANCE_TYPES_ID.CONCERT:
      return `${TP}.FN_PERFORMACE_TYPE_CONCERT`;
    case PERFORMANCE_TYPES_ID.NATIONAL_PREMIERE:
      return `${TP}.FN_PERFORMACE_TYPE_NATIONAL_PREMIERE`;
    case PERFORMANCE_TYPES_ID.NEW_PRODUCTION:
      return `${TP}.FN_PERFORMACE_TYPE_NEW_PRODUCTION`;
    case PERFORMANCE_TYPES_ID.FESTIVAL:
      return `${TP}.m_FESTIVAL`;
    case PERFORMANCE_TYPES_ID.WORLD_PREMIERE:
      return `${TP}.FN_PERFORMACE_TYPE_WORLD_PREMIERE`;
    case PERFORMANCE_TYPES_ID.MATINEE:
      return `${TP}.FN_PERFORMACE_TYPE_MATINEE`;
    case PERFORMANCE_TYPES_ID.TOUR:
      return `${TP}.FN_PERFORMACE_TYPE_TOUR`;
    case PERFORMANCE_TYPES_ID.DOUBLE_BILL:
      return `${TP}.FN_DOUBLE_TRIPLE_BILL`;
    case PERFORMANCE_TYPES_ID.CANCELLED:
      return `${TP}.PERFORMANCE_CANCELLED`;
    case PERFORMANCE_TYPES_ID.LIVESTREAM:
      return `${TP}.FN_LIVESTREAM`;
    case PERFORMANCE_TYPES_ID.RECITAL:
      return `${TP}.FN_RECITAL`;
    case PERFORMANCE_TYPES_ID.VIDEO_ON_DEMAND:
      return `${TP}.FN_VIDEO_ON_DEMAND`;
    default:
      return '';
  }
};

export const getIdObjectOrNull = value => (value ? { id: value } : null);

export const getUnusedLangsOptions = (items, languages) => {
  const idsOfUsedLangs = items?.map(d => d?.language?.id) || [];
  const langsToClean = [...languages];
  remove(langsToClean, l => idsOfUsedLangs.includes(l?.id));
  return createSelectFieldOptions(langsToClean, 'id', 'name', {}, true);
};

export const isCustomProductionName = production => production?.name?.length > 0;

export const romanize = num => {
  const lookup = { M: 1000, CM: 900, D: 500, CD: 400, C: 100, XC: 90, L: 50, XL: 40, X: 10, IX: 9, V: 5, IV: 4, I: 1 };
  let roman = '';
  let i;
  for (i in lookup) {
    while (num >= lookup[i]) {
      roman += i;
      num -= lookup[i];
    }
  }
  return roman;
};

export const updateMediaStructure = incomArray => {
  if (incomArray && isArray(incomArray)) {
    return incomArray.map(image => {
      const taggedProfiles = [];
      const otherTags = [];
      const tags = [...(image?.tags || [])];
      tags.forEach(tag => {
        if (
          tag?.tagType?.id === MEDIA_TAGS_TYPES_IDS.PROFILE &&
          (tag?.availabilityTypes || []).includes(TAGS_AVAILABILITY_TYPES.LABEL)
        ) {
          taggedProfiles.push(tag);
        } else {
          otherTags.push(tag);
        }
      });
      return {
        ...image,
        selectedProduction: get(
          (image?.tags || []).find(tag => tag?.tagType?.id === MEDIA_TAGS_TYPES_IDS.PRODUCTION),
          'production',
          null,
        ),
        profileTags: taggedProfiles,
        defaultTags: otherTags,
      };
    });
  }
  return [];
};

export const fetchAbortController = () => {
  let abortController;
  if (typeof window !== 'undefined' && AbortController) {
    abortController = new AbortController();
  }
  return abortController;
};

export const getTimezonesOptions = () =>
  rawTimeZones.map(el => ({
    label: `(${el.abbreviation} ${el.rawFormat.substr(0, 6)}), ${el?.name?.replace(/_/g, ' ')}`,
    value: el.name,
  }));

export const getAttributeName = (media, attrTypeId, field = 'name') =>
  get(find(media?.attributes, { attributeType: { id: attrTypeId } }), field);

export const getTagsByType = (tags, tagTypeId) => filter(tags, { tagType: { id: tagTypeId } });

export const getMediaTimeInfo = recordAttributes => ({
  startTime: getAttributeName(recordAttributes, VIDEO_ATTRIBUTE_TYPES_IDS.START_TIME),
  endTime: getAttributeName(recordAttributes, VIDEO_ATTRIBUTE_TYPES_IDS.END_TIME),
  timezone: getAttributeName(recordAttributes, VIDEO_ATTRIBUTE_TYPES_IDS.TIMEZONE),
  startDate: getAttributeName(recordAttributes, VIDEO_ATTRIBUTE_TYPES_IDS.START_DATE),
  endDate: getAttributeName(recordAttributes, VIDEO_ATTRIBUTE_TYPES_IDS.END_DATE),
});

export const filterComposers = composers =>
  composers && composers.length > 0 ? composers.filter(composer => composer.slug !== 'various') : [];

export const getProductionOwnerId = (typeId, production, entityId) => {
  if (production?.forEntity && production?.forEntityId) {
    if (production?.forEntity === typeId) {
      return production?.forEntityId;
    }
    return null;
  }
  return entityId;
};

export const checkDateForProductionDeletion = (allowedCountOfDates, publishedDate) => {
  const minDeleteDateForPublishedProduction = +allowedCountOfDates;
  let isDateValid = true;
  if (minDeleteDateForPublishedProduction || minDeleteDateForPublishedProduction === 0) {
    const edgeDateOfDeletion = new Date(publishedDate);
    edgeDateOfDeletion.setDate(edgeDateOfDeletion.getDate() + minDeleteDateForPublishedProduction);
    isDateValid = new Date() <= new Date(edgeDateOfDeletion);
  }
  return isDateValid;
};

export const canDoProductionAction = (permission, typeId, entityId, production) =>
  permission && entityId === getProductionOwnerId(typeId, production, entityId);

export const getDateValues = (values, date) =>
  pickBy(
    {
      day: values[date.day.name]?.value,
      month: values[date.month.name]?.value,
      year: values[date.year.name]?.value,
    },
    val => !isNil(val),
  );

export const validateDate = (value, context, date) => {
  const day = value?.value;
  const month = get(context.parent, date.month.name)?.value;
  const year = get(context.parent, date.year.name)?.value;
  if (day && month && year) {
    return createDate(`${month}/${day}/${year}`, 'M/DD/YYYY').isValid();
  }
  if (day && month) {
    if (month === 2) return day < 30;
    return createDate(`${month}/${day}/${createDate().year}`, 'M/DD/YYYY').isValid();
  }
  return true;
};

export const getDateDifference = (minDate, maxDate) =>
  createDate(minDate, 'YYYY.MM.DD').diff(createDate(maxDate, 'YYYY.MM.DD'), 'days');

export const getDateFormat = (minDate, maxDate) =>
  `${createDate(minDate).format("MMM'YY")} - ${createDate(maxDate).format("MMM'YY")}`;
export const getCurrentTimezoneDate = stringDate => {
  const [year, month, day] = stringDate.split('-');
  return new Date(+year, +month - 1, +day);
};

export const getPerformanceDate = (date, format) => createDate(date).format(format);

export const getArtistRoles = (production, profile) => {
  const SEPARATOR = ' / ';
  const casts = [
    ...get(production, 'casts', []),
    ...get(production, 'crew', []),
    ...get(production, 'other', []),
  ].filter(c => get(c, 'profile.id', '-1') === profile.id);

  if (casts.length === 0) {
    return null;
  }
  const roles = casts
    .map(cast =>
      cast.performer
        ? cast?.workRole?.original_name || cast?.workRole?.name
        : cast?.profession?.name || cast?.professionReference,
    )
    .filter(c => c && c.length > 0)
    .join(SEPARATOR);
  const type = casts
    .map(cast => get(cast, get(cast, 'profession.name', '') === 'Singer' ? 'professionQualification.name' : null))
    .filter(c => c && c.length > 0)
    .join(SEPARATOR);
  return { roles, type };
};

export const isFutureDatesExist = data =>
  data.find(o => createDate(o.date).format(dateFormats.basic) >= createDate().format(dateFormats.basic));

const getPeriodString = (date1, date2) => {
  const dates = compact([date1, date2]);
  return dates.map(d => createDate(d).format(dateFormats.shortMonthEvent)).join(' - ');
};

export const getCountryCity = data => {
  const country = getWithDefault(data, 'country.name', '');
  const city = getWithDefault(data, 'city.name', '');
  const commaOperator = country && city ? ',' : '';
  return `${city}${commaOperator} ${country}`;
};

export const getFestivalsInfo = festival => {
  const logo = get(festival, 'logo.medium', '');
  const name = get(festival, 'name', '');
  return {
    name,
    region: getCountryCity(festival),
    logo,
    date: getPeriodString(festival.min_date, festival.max_date),
    logoType: LOGO_TYPE.SQUARE,
  };
};

export const getCompanyInfo = company => {
  const logo = get(company, 'logo.medium', '');
  const name = get(company, 'name', '');
  return {
    name,
    region: getCountryCity(company),
    logo,
    logoType: LOGO_TYPE.CIRCLE,
  };
};

export const getRentalInfo = rental => ({
  name: get(rental, 'production.name', ''),
  region: getCountryCity(rental),
  logo: get(rental, 'image.medium', ''),
  logoType: LOGO_TYPE.CIRCLE,
  date: getPeriodString(rental?.dateFrom, rental?.dateTo),
  info: get(rental, 'company.name'),
});

const fetchOptionsForSubFilters = async (options, query) => {
  const { endpoint, ...restOfQuery } = query;
  const allPromises = options.map(option => {
    if (option.type === 'date') return { [option.type]: [{ id: '-1', name: option.label }] };
    return getOptions({ endpoint, query: { ...restOfQuery, aggregation_type: option.type } }).then(data => {
      if (data?.data?.length > 0) return { [option.type]: [{ id: '-1', name: option.label }, ...data.data] };
    });
  });
  return Promise.all(allPromises);
};

export const prefetchSubfilterOptions = async query => {
  const subFilterOptions = [{ type: 'language_id' }, { type: 'subtitle_id' }, { type: 'country_id' }, { type: 'date' }];
  await fetchOptionsForSubFilters(subFilterOptions, {
    endpoint: 'productions/aggregations',
    ...query,
  });
};

export const getPremierPerformances = types =>
  types?.filter(
    c =>
      c.id === PERFORMANCE_TYPES_ID.WORLD_PREMIERE ||
      c.id === PERFORMANCE_TYPES_ID.NATIONAL_PREMIERE ||
      c.id === PERFORMANCE_TYPES_ID.DOUBLE_BILL,
  );

function isValidDate(d) {
  return !isNaN(Date.parse(d));
}

/**
 * Set Date Range
 * useful to determine Production view vs Performance view
 * @param fromDate
 * @param toDate
 * @returns {{fromDate: Date, toDate: Date}}
 */
export const getValidDateRange = (fromDate, toDate) => {
  const fromDt = isValidDate(fromDate);
  const toDt = isValidDate(toDate);
  // no dates -> set to today
  if (!fromDt && !toDt) {
    return {
      dayFrom: createDate().format(dateFormats.basic),
      dayTo: createDate().format(dateFormats.basic),
    };
  }

  if (fromDt && !toDt) {
    return {
      dayFrom: dateSendFormat(fromDate),
      dayTo: createDate(fromDate)
        .add(1, 'years')
        .format('YYYY-MM-DD'),
    };
  }
  if (!fromDt && toDt) {
    return {
      dayFrom: createDate(toDate)
        .subtract(1, 'years')
        .format('YYYY-MM-DD'),
      dayTo: dateSendFormat(toDate),
    };
  }
  return {
    dayFrom: dateSendFormat(fromDate),
    dayTo: dateSendFormat(toDate),
  };
};

function isPresentAndTruthy(val) {
  return typeof val !== 'undefined' && val;
}

/**
 * If all of the viewby filters are true , dont send any viewBy filters
 * if one or more of them is true, send the respective values only
 * filters accounted for live_stream and tickets
 * This is where tickets->has_tickets and live_stream -> has_live_stream used
 * to transform before sending it to API
 * @param query
 */
export const conditionallyRemoveViewByFilters = query => {
  const { live_stream, tickets, ...rest } = query;
  let finalQuery = { ...rest };
  // Uncomment below to make live_stream logic to work
  // if (isPresentAndTruthy(live_stream) && isPresentAndTruthy(tickets)) {
  //   return rest;
  // }
  // if (isPresentAndTruthy(live_stream)) {
  //   finalQuery = { ...finalQuery, has_live_stream: live_stream };
  // }
  if (isPresentAndTruthy(tickets)) {
    finalQuery = { ...finalQuery, has_tickets: tickets };
  }
  return finalQuery;
};

/**
 * Same logic as conditionallyRemoveViewByFilters
 * Retains "tickets" as query params instead of has_tickets
 * @param query
 * @returns {{tickets: *}}
 */
export const conditionallyRemoveViewByFiltersForRoute = query => {
  const { live_stream, tickets, ...rest } = query;
  let finalQuery = { ...rest };
  // Uncomment below to make live_stream logic to work
  // if (isPresentAndTruthy(live_stream) && isPresentAndTruthy(tickets)) {
  //   return rest;
  // }
  // if (isPresentAndTruthy(live_stream)) {
  //   finalQuery = { ...finalQuery, live_stream: live_stream };
  // }
  if (isPresentAndTruthy(tickets)) {
    finalQuery = { ...finalQuery, tickets };
  }
  return finalQuery;
};

export const isRedMask = identifier =>
  [PRODUCTION_ADDED_IDENTIFIERS_IDS.ADMIN, PRODUCTION_ADDED_IDENTIFIERS_IDS.COMPANY].includes(identifier);

export const getWorkRoleTranslationName = performance => {
  const work = performance?.work ? performance?.work : performance.workRole;
  return work?.name && work?.name !== work?.original_name ? work?.name : null;
};

export const getWorkRoleCanonicalName = performance => {
  const work = performance?.work ? performance?.work : performance.workRole;
  return work?.original_name || work?.name;
};

/**
 * Converts array to a string with `and`
 *
 * @param t
 * @param data
 * @param separator
 *
 * @returns {string}
 */
export const arrayToStringWithAnd = (t, data, separator = ', ') => {
  if (!Array.isArray(data) || typeof t !== 'function') {
    return '';
  }
  if (data.length === 0) {
    return '';
  }
  if (data.length === 1) {
    return data[0];
  }
  const lastItem = data.pop();
  const and = t(`${TP}.AND`);
  return `${data.join(separator)} ${and} ${lastItem}`;
};

export const isPastDate = date => {
  const momentDate = createDate(date);
  return momentDate.isValid() ? momentDate.isBefore(createDate(), 'day') : false;
};

export const getProductionName = production => production?.name || production?.work?.original_name;

export const pastDateUpTo3Years = createDate()
  .subtract(13, 'year')
  .toDate();
export const futureDateUpTo1Years = createDate()
  .add(11, 'year')
  .toDate();

export const getWithDefault = (obj, valuePath, defaultValue) => defaultTo(get(obj, valuePath), defaultValue);
